import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { Button, Divider, VStack } from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import { SimpleTable } from '../../tables';
import { tableDataUtils } from '../../../utils';
import { JsFieldInspector } from '../../jsFormBase';
import { useJsFieldFormData } from '../../../hooks';
import { JsFormConfigContext } from '../../../contexts';
import { IconButton } from '../../buttons';
/**
 * Component renders "/schemas/jsf-table-add-rows" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is TTableAddRowsFormData<T>.
 */
const TableAddRowsJsFieldFC = (props) => {
    const { isInspector } = useContext(JsFormConfigContext) ?? {};
    const { fieldConfig, fieldValues, onChangeFieldValues } = useJsFieldFormData({
        config: props.formData?.fieldConfig ?? null,
        values: props.formData?.fieldValues ?? null,
        onChange: props.onChange,
        watchPropsChanges: true
    });
    const handleAdd = () => {
        if (!fieldConfig) {
            return;
        }
        const newRowValue = tableDataUtils.createEmptyItem(fieldConfig.columns);
        const newValues = [
            ...(fieldValues ?? []),
            { key: uuidv4(), value: newRowValue }
        ];
        onChangeFieldValues(newValues);
    };
    const handleRemoveRow = useCallback((deleteId) => {
        if (!fieldConfig?.removable || !fieldValues) {
            return;
        }
        const newValues = tableDataUtils.removeValueAtId(fieldValues, deleteId);
        onChangeFieldValues(newValues);
    }, [fieldConfig?.removable, fieldValues, onChangeFieldValues]);
    const colums = useMemo(() => {
        if (!fieldConfig?.columns) {
            return [];
        }
        if (!fieldConfig?.removable ||
            (fieldValues?.length && fieldValues.length <= fieldConfig?.minRowsNum)) {
            return fieldConfig.columns;
        }
        return [
            ...fieldConfig.columns,
            {
                name: '',
                width: '28px',
                sticky: true,
                render: (rowId) => (_jsx(IconButton, { icon: "Cross", onClick: () => handleRemoveRow(rowId) }))
            }
        ];
    }, [
        fieldConfig?.columns,
        fieldConfig?.minRowsNum,
        fieldConfig?.removable,
        fieldValues?.length,
        handleRemoveRow
    ]);
    const handleValueChange = (rowId, newValue) => {
        if (!fieldValues) {
            return;
        }
        const editedValue = { ...fieldValues[rowId], value: newValue };
        const newTableData = tableDataUtils.changeValueById(fieldValues, editedValue, rowId);
        onChangeFieldValues(newTableData);
    };
    const isReadOnly = !!props.schema.readOnly;
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(VStack, { "data-testid": "TableAddRowsJsField-test", "data-name": props.name, w: "100%", gap: "8px", pb: "2px", children: [_jsx(SimpleTable, { columns: colums, rows: fieldValues ?? [], isReadOnly: isReadOnly, noItemsTitle: fieldConfig?.noRowsText, onValueChange: handleValueChange }), !props.schema.readOnly && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(Button, { variant: "ghost", size: "smSquare", w: "100%", onClick: handleAdd, children: fieldConfig?.addText })] }))] }) }));
};
export const TableAddRowsJsField = memo(TableAddRowsJsFieldFC);
//# sourceMappingURL=TableAddRowsJsField.js.map